import { useEffect, useRef, useState } from 'react'
import { ThreeDS } from './paymentCard'
import criarRegistroListeners3DS, { Handlers3DS } from './registrarListeners3DS'
import criarWatchdog3DS from './watchdog3DS'

interface Safe2PayMpi {
  addEventListener: (evento: string, callback: (e: unknown) => void) => void
  Init: (
    config: {
      IsEnabled: boolean
      IsSandbox: boolean
      IsDebug: boolean
      IsChallengeSuppressed: boolean
    },
    amount: number
  ) => void
  Checkout: (paymentObject: {
    currency: string
    installments: string
    cardnumber: string
    cardexpirationmonth: string
    cardexpirationyear: string
    cardalias: string
  }) => void
}

declare global {
  interface Window {
    Safe2Pay?: { Mpi?: Safe2PayMpi }
  }
}

export interface DadosCartao3DS {
  numero: string
  mes: string
  ano: string
  titular: string
}

export interface IniciarDesafioParams {
  cartao: DadosCartao3DS
}

interface Use3DSCallbacks {
  onSuccess: (threeDS: ThreeDS) => void
  onFailure: (evento: unknown) => void
  onError: (evento: unknown) => void
  /** Cartão sem 3DS ou desconhecido no diretório: o banco não autentica. */
  onUnenrolled: (evento: unknown) => void
  /** Bandeira do cartão não suportada pela autenticação. */
  onUnsupportedBrand: (evento: unknown) => void
  /** Nenhum evento chegou em TIMEOUT_3DS_MS depois do `Checkout`. */
  onTimeout: () => void
}

interface Mpi3DSSuccessEvent {
  Cavv: string
  Xid?: string | null
  Eci: string
  Version: string
  ReferenceId?: string | null
}

export default function use3DS(callbacks: Use3DSCallbacks) {
  const cbRef = useRef(callbacks)
  cbRef.current = callbacks

  const [watchdog] = useState(criarWatchdog3DS)
  const [registro] = useState(criarRegistroListeners3DS)
  const [handlers] = useState<Handlers3DS>(() => ({
    onSuccess: (evento: unknown) => {
      watchdog.limpar()
      const e = evento as Mpi3DSSuccessEvent
      cbRef.current.onSuccess({
        Cavv: e.Cavv,
        Xid: e.Xid ?? null,
        Eci: e.Eci,
        Version: e.Version,
        ReferenceId: e.ReferenceId ?? null,
      })
    },
    onFailure: (e: unknown) => {
      watchdog.limpar()
      cbRef.current.onFailure(e)
    },
    onError: (e: unknown) => {
      watchdog.limpar()
      cbRef.current.onError(e)
    },
    onUnenrolled: (e: unknown) => {
      watchdog.limpar()
      cbRef.current.onUnenrolled(e)
    },
    onUnsupportedBrand: (e: unknown) => {
      watchdog.limpar()
      cbRef.current.onUnsupportedBrand(e)
    },
    // Os dois só ocorrem com uma configuração de `Init` que não usamos; se
    // ocorrerem, encerram a tentativa como erro técnico em vez de esperar o
    // watchdog.
    onDisabled: (e: unknown) => {
      watchdog.limpar()
      cbRef.current.onError(e)
    },
    onChallengeSuppression: (e: unknown) => {
      watchdog.limpar()
      cbRef.current.onError(e)
    },
  }))

  // Separado do registro dos listeners: tem de valer mesmo quando o SDK não
  // estava carregado no mount.
  useEffect(() => () => watchdog.limpar(), [watchdog])

  // No mount, quando o SDK já está lá. Se ele carregar depois, `inicializar`
  // e `iniciarDesafio` registram antes de usá-lo.
  useEffect(() => {
    registro.garantir(window.Safe2Pay?.Mpi, handlers)
  }, [registro, handlers])

  function inicializar(amount: number): boolean {
    const mpi = window.Safe2Pay?.Mpi
    if (!mpi) return false

    registro.garantir(mpi, handlers)
    mpi.Init(
      {
        IsEnabled: true,
        IsSandbox: false,
        IsDebug: false,
        IsChallengeSuppressed: false,
      },
      amount
    )

    return true
  }

  function iniciarDesafio({ cartao }: IniciarDesafioParams): boolean {
    const mpi = window.Safe2Pay?.Mpi
    if (!mpi) return false

    registro.garantir(mpi, handlers)
    watchdog.armar(() => cbRef.current.onTimeout())

    mpi.Checkout({
      currency: 'BRL',
      installments: '01',
      cardnumber: cartao.numero,
      cardexpirationmonth: cartao.mes,
      cardexpirationyear: cartao.ano,
      cardalias: cartao.titular,
    })

    return true
  }

  return { inicializar, iniciarDesafio }
}